import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MangaCard from '@/components/base/MangaCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { mangaList } from '@/mocks/manga';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const manga = mangaList.find((m) => m.slug === slug);

  if (!manga) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 text-center">
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-file-unknow-line text-3xl"></i>
              </div>
              <h1 className="mt-6 text-2xl font-heading font-bold text-foreground-900">漫画未找到</h1>
              <p className="mt-2 text-foreground-500">抱歉，您要找的漫画不存在或已被移除。</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 mt-6 bg-primary-500 text-background-50 px-6 py-2.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-home-line"></i>
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const catName = categories.find((c) => c.slug === manga.category)?.name || manga.category;
  const relatedManga = mangaList
    .filter((m) => m.category === manga.category && m.slug !== manga.slug)
    .slice(0, 4);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - 韩漫画网`,
    description: manga.description,
    author: { '@type': 'Person', name: manga.author },
    datePublished: '2026-01-01',
    dateModified: manga.updateDate,
    image: manga.coverImage,
    publisher: { '@type': 'Organization', name: '韩漫画网' },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanmanhua.com/' },
      { '@type': 'ListItem', position: 2, name: catName, item: `https://hanmanhua.com/category/${manga.category}` },
      { '@type': 'ListItem', position: 3, name: manga.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: catName, href: `/category/${manga.category}` },
                { label: manga.title },
              ]}
            />
          </div>

          {/* Manga Detail Header */}
          <article className="max-w-7xl mx-auto px-4 md:px-6 pb-8">
            <div className="flex flex-col md:flex-row gap-6 md:gap-10">
              {/* Cover */}
              <div className="flex-shrink-0 w-full md:w-[280px]">
                <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                  <img
                    src={manga.coverImage}
                    alt={`${manga.title} - 免费漫画在线阅读`}
                    title={`${manga.title} - 韩国漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {manga.title}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <span className="text-sm text-foreground-500">
                    <i className="ri-user-line mr-1"></i>
                    {manga.author}
                  </span>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${
                    manga.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {manga.status}
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-4 md:gap-6">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="text-lg font-semibold text-foreground-800">{manga.rating}</span>
                    <span className="text-sm text-foreground-500">评分</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-eye-line text-foreground-400"></i>
                    <span className="font-semibold text-foreground-800">
                      {manga.views >= 10000 ? `${(manga.views / 10000).toFixed(1)}万` : manga.views}
                    </span>
                    <span className="text-sm text-foreground-500">阅读</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-file-list-3-line text-foreground-400"></i>
                    <span className="font-semibold text-foreground-800">{manga.chapters}</span>
                    <span className="text-sm text-foreground-500">话</span>
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  {manga.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed">
                  {manga.description}
                </p>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-2.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-line"></i>
                    开始阅读
                  </button>
                  <button className="inline-flex items-center gap-2 bg-background-100 text-foreground-700 px-6 py-2.5 rounded-full text-sm font-medium border border-background-200/70 hover:border-primary-300 hover:text-primary-500 transition-all whitespace-nowrap cursor-pointer">
                    <i className="ri-heart-line"></i>
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </article>

          {/* Chapter List */}
          <section className="bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                章节列表
              </h2>
              <div className="bg-background-50 rounded-lg border border-background-200/70 divide-y divide-background-200/70 max-h-[500px] overflow-y-auto">
                {manga.chapterList.map((ch) => (
                  <button
                    key={ch.id}
                    className="w-full flex items-center justify-between px-4 md:px-6 py-3.5 hover:bg-primary-50 transition-colors text-left cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-foreground-400 font-mono whitespace-nowrap">
                        #{ch.id}
                      </span>
                      <span className="text-sm text-foreground-700 whitespace-nowrap">{ch.title}</span>
                    </div>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.date}</span>
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Related Manga */}
          {relatedManga.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {relatedManga.map((m) => (
                  <MangaCard key={m.slug} manga={m} />
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}